<template>
  <div class="inline-flex rounded-full border border-stroke-strong dark:border-stroke-strong-dark
              divide-x divide-stroke-strong dark:divide-stroke-strong-dark overflow-hidden">
    <button
      v-for="opt in visibleOptions"
      :key="opt.status"
      @click="$emit('select', opt.status)"
      @animationend="opt.status === pulseStatus && $emit('pulse-end')"
      class="flex items-center gap-1 px-3 py-1.5 text-xs font-medium transition-colors"
      :class="[
        optionClass(opt),
        opt.status === pulseStatus ? 'attended-pop-animation' : ''
      ]"
    >
      <component :is="opt.icon" class="h-3.5 w-3.5" />
      {{ visibleOptions.length === 1 && !isSelected(opt.status) ? `Mark ${opt.label}` : opt.label }}
    </button>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { CheckCircleIcon, ExclamationCircleIcon, XCircleIcon } from '@heroicons/vue/24/outline'

const props = defineProps({
  status: { type: String, default: 'not reported' }, // current attendance value for this student
  isEditing: { type: Boolean, default: false }, // re-editing an already-recorded status: highlight the active choice
  pulseStatus: { type: String, default: null }, // status to play the confirm-pop animation on, if any
  // Which options to render, e.g. ['excused'] when the lesson is too far out for attended/absent
  // (backend guard: lessons.py _lesson_is_future). With one entry this collapses to a plain rounded
  // button instead of a segmented pill.
  availableStatuses: { type: Array, default: () => ['absent', 'excused', 'attended'] }
})
defineEmits(['select', 'pulse-end'])

const options = [
  {
    status: 'absent',
    label: 'Absent',
    icon: XCircleIcon,
    restClass: 'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20',
    selectedClass: 'bg-red-600 text-white'
  },
  {
    status: 'excused',
    label: 'Excused',
    icon: ExclamationCircleIcon,
    restClass: 'text-yellow-700 dark:text-yellow-400 hover:bg-yellow-50 dark:hover:bg-yellow-900/20',
    selectedClass: 'bg-yellow-600 text-white'
  },
  {
    status: 'attended',
    label: 'Attended',
    icon: CheckCircleIcon,
    restClass: 'text-green-700 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20',
    selectedClass: 'bg-green-600 text-white'
  }
]

const visibleOptions = computed(() => options.filter(opt => props.availableStatuses.includes(opt.status)))

const isSelected = (status) => props.isEditing && props.status === status

// A lone option (e.g. "Mark Excused" before the 24h window opens) isn't a real choice among
// alternatives, so it's muted gray instead of the status color to avoid implying a decision was made.
const MUTED_REST = 'text-fg-secondary dark:text-fg-secondary-dark hover:bg-surface-muted dark:hover:bg-surface-hover-dark'
const MUTED_SELECTED = 'bg-fg-muted dark:bg-fg-muted-dark text-white'

const optionClass = (opt) => {
  if (visibleOptions.value.length === 1) return isSelected(opt.status) ? MUTED_SELECTED : MUTED_REST
  return isSelected(opt.status) ? opt.selectedClass : opt.restClass
}
</script>

<style scoped>
@keyframes button-pop {
  0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.7); }
  50% { transform: scale(1.05); box-shadow: 0 0 0 10px rgba(34, 197, 94, 0); }
  100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(34, 197, 94, 0); }
}
.attended-pop-animation {
  animation: button-pop 0.4s ease-out;
}
</style>
